import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useProject } from '@/contexts/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Landmark, ArrowLeft, RefreshCw, Loader2, TrendingUp, TrendingDown,
  DollarSign, Activity, Edit3, X, Save, Plus, Trash2, CalendarPlus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import ModuleInsightsPanel from '@/components/common/ModuleInsightsPanel';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ComposedChart, ReferenceLine
} from 'recharts';

function KPICard({ title, value, subtitle, icon: Icon, color = '#F97316' }) {
  return (
    <div className="rounded-xl p-4" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtitle}</div>}
    </div>
  );
}

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

/* ── Cash Flow Edit Wizard ── */
function CashFlowEditWizard({ projectId, period, onClose, onSaved }) {
  const [form, setForm] = useState({
    outflowPlanned: period.outflows.planned,
    outflowActual: period.outflows.actual,
    outflowForecast: period.outflows.forecast || 0,
    inflowPlanned: period.inflows.planned,
    inflowActual: period.inflows.actual,
    inflowForecast: period.inflows.forecast || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/v1/projects/${projectId}/cc/cash-flow/period`, {
        periodId: period.periodId, ...form
      });
      toast.success(`Cash flow updated for ${period.periodLabel}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update cash flow');
    } finally {
      setSaving(false);
    }
  };

  const netActual = form.inflowActual - form.outflowActual;
  const netForecast = form.inflowForecast - form.outflowForecast;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6 mx-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="cashflow-edit-wizard">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Edit Cash Flow</h2>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{period.periodLabel}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:opacity-70"><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="space-y-4">
          {/* Outflows */}
          <div className="text-xs font-semibold flex items-center gap-2" style={{ color: '#EF4444' }}>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#EF4444' }} /> Outflows
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] mb-1 block" style={{ color: 'var(--text-muted)' }}>Planned</label>
              <Input type="number" value={form.outflowPlanned} onChange={e => setForm(f => ({ ...f, outflowPlanned: parseFloat(e.target.value) || 0 }))} className="text-xs h-8" data-testid="outflow-planned-input" />
            </div>
            <div>
              <label className="text-[10px] mb-1 block" style={{ color: 'var(--text-muted)' }}>Actual</label>
              <Input type="number" value={form.outflowActual} onChange={e => setForm(f => ({ ...f, outflowActual: parseFloat(e.target.value) || 0 }))} className="text-xs h-8" data-testid="outflow-actual-input" />
            </div>
            <div>
              <label className="text-[10px] mb-1 block" style={{ color: 'var(--text-muted)' }}>Forecast</label>
              <Input type="number" value={form.outflowForecast} onChange={e => setForm(f => ({ ...f, outflowForecast: parseFloat(e.target.value) || 0 }))} className="text-xs h-8" data-testid="outflow-forecast-input" />
            </div>
          </div>

          {/* Inflows */}
          <div className="text-xs font-semibold flex items-center gap-2" style={{ color: '#22C55E' }}>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#22C55E' }} /> Inflows
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] mb-1 block" style={{ color: 'var(--text-muted)' }}>Planned</label>
              <Input type="number" value={form.inflowPlanned} onChange={e => setForm(f => ({ ...f, inflowPlanned: parseFloat(e.target.value) || 0 }))} className="text-xs h-8" data-testid="inflow-planned-input" />
            </div>
            <div>
              <label className="text-[10px] mb-1 block" style={{ color: 'var(--text-muted)' }}>Actual</label>
              <Input type="number" value={form.inflowActual} onChange={e => setForm(f => ({ ...f, inflowActual: parseFloat(e.target.value) || 0 }))} className="text-xs h-8" data-testid="inflow-actual-input" />
            </div>
            <div>
              <label className="text-[10px] mb-1 block" style={{ color: 'var(--text-muted)' }}>Forecast</label>
              <Input type="number" value={form.inflowForecast} onChange={e => setForm(f => ({ ...f, inflowForecast: parseFloat(e.target.value) || 0 }))} className="text-xs h-8" data-testid="inflow-forecast-input" />
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="text-[11px] rounded-lg p-3" style={{ backgroundColor: 'var(--app-bg)', color: 'var(--text-muted)' }}>
              Net Actual: <strong style={{ color: netActual >= 0 ? '#22C55E' : '#EF4444' }}>{fmt(netActual)}</strong>
            </div>
            <div className="text-[11px] rounded-lg p-3" style={{ backgroundColor: 'var(--app-bg)', color: 'var(--text-muted)' }}>
              Net Forecast: <strong style={{ color: netForecast >= 0 ? '#22C55E' : '#EF4444' }}>{fmt(netForecast)}</strong>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-cashflow-btn"
            style={{ backgroundColor: '#F97316', color: '#fff' }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Add Month Wizard ── */
function AddMonthWizard({ projectId, existingPeriods, onClose, onAdded }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [saving, setSaving] = useState(false);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const periodId = `${year}-${String(month).padStart(2, '0')}`;
  const periodLabel = `${monthNames[month - 1]} ${year}`;
  const alreadyExists = existingPeriods.some(p => p.periodId === periodId);

  const handleAdd = async () => {
    if (alreadyExists) { toast.error('This month already exists'); return; }
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/cc/cash-flow/period`, { periodId, periodLabel });
      toast.success(`Added ${periodLabel}`);
      onAdded();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add month');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6 mx-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="add-month-wizard">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Add Month</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:opacity-70"><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[10px] mb-1 block" style={{ color: 'var(--text-muted)' }}>Year</label>
            <Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value) || 2026)} className="text-sm" min={2020} max={2035} data-testid="add-month-year" />
          </div>
          <div>
            <label className="text-[10px] mb-1 block" style={{ color: 'var(--text-muted)' }}>Month</label>
            <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
              className="w-full rounded-lg px-3 py-2 text-sm h-9" data-testid="add-month-month"
              style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
              {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="text-xs rounded-lg p-3 mb-4" style={{ backgroundColor: 'var(--app-bg)', color: 'var(--text-muted)' }}>
          Period: <strong style={{ color: 'var(--text-primary)' }}>{periodLabel}</strong> ({periodId})
          {alreadyExists && <span className="ml-2 text-[10px] font-medium" style={{ color: '#EF4444' }}>Already exists</span>}
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleAdd} disabled={saving || alreadyExists} data-testid="confirm-add-month-btn"
            style={{ backgroundColor: '#22C55E', color: '#fff' }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Add Month
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function WorkingCapitalManagement() {
  const navigate = useNavigate();
  const { projectId: ctxProjectId } = useProject();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [cashFlow, setCashFlow] = useState(null);
  const [wcMetrics, setWcMetrics] = useState(null);
  const [editPeriod, setEditPeriod] = useState(null);
  const [showAddMonth, setShowAddMonth] = useState(false);
  const [deletingPeriod, setDeletingPeriod] = useState(null);

  useEffect(() => {
    api.get('/v1/cc/projects-with-data').then(r => {
      setProjects(r.data.projects || []);
      if (r.data.projects?.length > 0) setSelectedProject(r.data.projects[0].project_id);
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const [cfRes, wcRes, cbsRes] = await Promise.all([
        api.get(`/v1/projects/${selectedProject}/cc/cash-flow/current`).catch(() => null),
        api.get(`/v1/projects/${selectedProject}/cc/cash-flow/working-capital`).catch(() => ({ data: {} })),
        api.get(`/v1/projects/${selectedProject}/cc/cash-flow/cbs-schedule-forecast`).catch(() => ({ data: null })),
      ]);
      let cf = cfRes?.data;
      if (!cf || !cf.periods) {
        const genRes = await api.post(`/v1/projects/${selectedProject}/cc/cash-flow/generate`);
        cf = genRes.data;
      }
      // Overlay the CBS+Schedule forecast into Plan Out / Fcst Out on any
      // month whose manual value is zero so the two tables no longer
      // disagree.  Actuals + inflows stay untouched (manual truth).
      const cbsPeriods = cbsRes?.data?.periods || [];
      if (cf?.periods && cbsPeriods.length > 0) {
        const cbsById = Object.fromEntries(cbsPeriods.map(p => [p.periodId, p]));
        cf.periods = cf.periods.map(p => {
          const src = cbsById[p.periodId];
          if (!src) return p;
          const planned = (p.outflows?.planned || 0) > 0 ? p.outflows.planned : src.plannedOutflow;
          const forecast = (p.outflows?.forecast || 0) > 0 ? p.outflows.forecast : src.forecastOutflow;
          return { ...p, outflows: { ...p.outflows, planned, forecast, _cbsDriven: (p.outflows?.planned || 0) === 0 } };
        });
      }
      setCashFlow(cf);
      setWcMetrics(wcRes?.data || {});
    } catch {
      toast.error('Failed to load cash flow data');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRemovePeriod = async (periodId) => {
    setDeletingPeriod(periodId);
    try {
      await api.delete(`/v1/projects/${selectedProject}/cc/cash-flow/period/${periodId}`);
      toast.success(`Removed period ${periodId}`);
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove period');
    } finally {
      setDeletingPeriod(null);
    }
  };

  const cumulativeData = useMemo(() => {
    if (!cashFlow?.periods) return [];
    return cashFlow.periods.map(p => ({
      period: p.periodLabel,
      plannedOutflow: Math.round(p.outflows.cumulativePlanned),
      actualOutflow: Math.round(p.outflows.cumulativeActual),
      forecastOutflow: Math.round(p.outflows.cumulativeForecast),
      plannedInflow: Math.round(p.inflows.cumulativePlanned),
      actualInflow: Math.round(p.inflows.cumulativeActual),
      forecastInflow: Math.round(p.inflows.cumulativeForecast || 0),
    }));
  }, [cashFlow]);

  const monthlyData = useMemo(() => {
    if (!cashFlow?.periods) return [];
    return cashFlow.periods.map(p => ({
      period: p.periodLabel,
      outflow: Math.round(p.outflows.actual || p.outflows.forecast),
      inflow: Math.round(p.inflows.actual || p.inflows.forecast),
      net: Math.round(p.netCashFlow),
      wcRequired: Math.round(p.workingCapitalRequired),
    }));
  }, [cashFlow]);

  const summaryInfo = cashFlow?.summary || {};

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="WORKING_CAPITAL" defaultOpen={true} compact={true} />}

      <div className="space-y-5" data-testid="working-capital-page">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="p-2.5 rounded-xl" style={{ background: '#F9731612', border: '1px solid #F9731620' }}>
            <Landmark className="w-5 h-5" style={{ color: '#F97316' }} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Working Capital Management</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Cash flow forecasting and funding requirements</p>
          </div>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm" data-testid="project-selector"
            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-text)' }} /></div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="wc-kpis">
              <KPICard title="Peak Funding Required" value={fmt(wcMetrics?.peakFunding)} subtitle={`Peak month: ${wcMetrics?.peakMonth || 'N/A'}`} icon={TrendingUp} color="#EF4444" />
              <KPICard title="Current Working Capital" value={fmt(wcMetrics?.currentWC)} icon={DollarSign} color="#F97316" />
              <KPICard title="Avg Monthly Outflow" value={fmt(wcMetrics?.avgMonthlyOutflow)} icon={TrendingDown} color="#143A6B" />
              <KPICard title="Total Project Outflow" value={fmt(summaryInfo.totalProjectOutflow)} subtitle={`Inflow: ${fmt(summaryInfo.totalProjectInflow)}`} icon={Activity} color="#0EA5E9" />
            </div>

            {/* Cumulative S-Curve */}
            <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="cumulative-scurve">
              <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Cumulative Cash Flow S-Curve</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={cumulativeData} margin={{ top: 10, right: 30, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="period" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => fmt(v)} />
                  <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} formatter={(v) => fmt(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="plannedOutflow" name="Planned Outflow" stroke="#3B82F6" strokeWidth={2} dot={false} strokeDasharray="6 3" />
                  <Line type="monotone" dataKey="actualOutflow" name="Actual Outflow" stroke="#EF4444" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="forecastOutflow" name="Forecast Outflow" stroke="#F59E0B" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                  <Line type="monotone" dataKey="plannedInflow" name="Planned Inflow" stroke="#22C55E" strokeWidth={2} dot={false} strokeDasharray="6 3" />
                  <Line type="monotone" dataKey="actualInflow" name="Actual Inflow" stroke="#10B981" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="forecastInflow" name="Forecast Inflow" stroke="#A78BFA" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="monthly-cashflow-chart">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Monthly Outflow vs Inflow</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={monthlyData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="period" tick={{ fill: 'var(--text-muted)', fontSize: 9 }} angle={-30} textAnchor="end" height={50} />
                    <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => fmt(v)} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="outflow" name="Outflow" fill="#EF4444" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="inflow" name="Inflow" fill="#22C55E" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="wc-requirement-chart">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Working Capital Requirement</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={monthlyData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="period" tick={{ fill: 'var(--text-muted)', fontSize: 9 }} angle={-30} textAnchor="end" height={50} />
                    <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => fmt(v)} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <ReferenceLine y={0} stroke="var(--text-muted)" strokeDasharray="3 3" />
                    <Bar dataKey="net" name="Net Cash Flow" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                    <Line type="monotone" dataKey="wcRequired" name="WC Required" stroke="#F97316" strokeWidth={2.5} dot={{ r: 2, fill: '#F97316' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Monthly Data Table */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="cashflow-table">
              <div className="p-4 flex items-center justify-between flex-wrap gap-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <div>
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Monthly Cash Flow Detail</h3>
                  <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>{cashFlow?.periods?.length || 0} months | Click row to edit</span>
                </div>
                <Button size="sm" onClick={() => setShowAddMonth(true)} data-testid="add-month-btn"
                  style={{ backgroundColor: '#22C55E', color: '#fff' }}>
                  <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Add Month
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--table-header-bg)' }}>
                      {['Period', 'Plan Out', 'Actual Out', 'Fcst Out', 'Plan In', 'Actual In', 'Fcst In', 'Net Flow', 'WC Req.', ''].map(h => (
                        <th key={h} className={`p-2.5 font-medium whitespace-nowrap ${h === 'Period' ? 'text-left' : h === '' ? 'w-16 text-center' : 'text-right'}`} style={{ color: 'var(--text-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(cashFlow?.periods || []).map((p, i) => (
                      <tr key={p.periodId} className="group transition-colors"
                        style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--table-row-alt)' }}>
                        <td className="p-2.5 font-medium whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{p.periodLabel}</td>
                        <td className="p-2.5 text-right" style={{ color: 'var(--text-secondary)' }}>{fmt(p.outflows.planned)}</td>
                        <td className="p-2.5 text-right font-medium" style={{ color: p.outflows.actual > 0 ? '#EF4444' : 'var(--text-disabled)' }}>{fmt(p.outflows.actual)}</td>
                        <td className="p-2.5 text-right" style={{ color: p.outflows.forecast > 0 ? '#F59E0B' : 'var(--text-disabled)' }}>{fmt(p.outflows.forecast)}</td>
                        <td className="p-2.5 text-right" style={{ color: 'var(--text-secondary)' }}>{fmt(p.inflows.planned)}</td>
                        <td className="p-2.5 text-right font-medium" style={{ color: p.inflows.actual > 0 ? '#22C55E' : 'var(--text-disabled)' }}>{fmt(p.inflows.actual)}</td>
                        <td className="p-2.5 text-right" style={{ color: p.inflows.forecast > 0 ? '#A78BFA' : 'var(--text-disabled)' }}>{fmt(p.inflows.forecast)}</td>
                        <td className="p-2.5 text-right font-medium" style={{ color: p.netCashFlow >= 0 ? '#22C55E' : '#EF4444' }}>{fmt(p.netCashFlow)}</td>
                        <td className="p-2.5 text-right font-medium" style={{ color: p.workingCapitalRequired > 0 ? '#F97316' : 'var(--text-disabled)' }}>{fmt(p.workingCapitalRequired)}</td>
                        <td className="p-2.5 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setEditPeriod(p)} className="p-1 rounded hover:bg-[var(--app-bg)]" data-testid={`edit-row-${p.periodId}`}>
                              <Edit3 className="w-3.5 h-3.5" style={{ color: '#0EA5E9' }} />
                            </button>
                            <button onClick={() => handleRemovePeriod(p.periodId)} disabled={deletingPeriod === p.periodId}
                              className="p-1 rounded hover:bg-[var(--app-bg)]" data-testid={`remove-row-${p.periodId}`}>
                              {deletingPeriod === p.periodId ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: '#EF4444' }} /> : <Trash2 className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Wizards */}
        {editPeriod && <CashFlowEditWizard projectId={selectedProject} period={editPeriod} onClose={() => setEditPeriod(null)} onSaved={loadData} />}
        {showAddMonth && <AddMonthWizard projectId={selectedProject} existingPeriods={cashFlow?.periods || []} onClose={() => setShowAddMonth(false)} onAdded={loadData} />}

        {/* CBS + Schedule Driven Cashflow Forecast */}
        {selectedProject && (
          <CbsScheduleCashflowSection projectId={selectedProject} />
        )}

        {selectedProject && (
          <ModuleInsightsPanel projectId={selectedProject} moduleKey="wcm" color="#14B8A6" deepLinkBase="/project-controls/cost/working-capital" />
        )}
      </div>
    </DashboardLayout>
  );
}

function CbsScheduleCashflowSection({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retentionPct, setRetentionPct] = useState(5);
  const [retentionLag, setRetentionLag] = useState(6);
  const [elemView, setElemView] = useState(true);
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/cc/cash-flow/cbs-schedule-forecast`, { params: { retentionPct, retentionLagMonths: retentionLag } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [projectId, retentionPct, retentionLag]);

  if (loading) return null;
  if (!data || (data.periods || []).length === 0) {
    return (
      <div className="rounded-xl p-4 text-xs text-center" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
        <b>CBS + Schedule Cashflow:</b> {data?.note || 'Link CBS rows to macro-schedule activities in the CBS Matrix to see a P/F/A cashflow driven by those links.'}
      </div>
    );
  }
  const fmt = (v) => v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const elements = data.elements || [];
  const peak = data.peakFunding || {};
  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="wc-cbs-sched-section">
      {/* Header + controls */}
      <div className="p-3 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: 'var(--border-color)' }}>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>CBS + Schedule Cashflow Forecast</h3>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Distributes each linked CBS row's budget across its schedule window and fans it across Manpower / Material / Equipment / Subcontract.</div>
        </div>
        <div className="flex items-center gap-2 text-[10px] flex-wrap" style={{ color: 'var(--text-muted)' }}>
          <span className="px-2 py-1 rounded font-semibold" style={{ background: '#0EA5E922', color: '#0EA5E9' }}>{data.summary?.totalCbsRowsLinked || 0} CBS rows linked</span>
          <span className="px-2 py-1 rounded font-semibold" style={{ background: '#22C55E22', color: '#16A34A' }}>Total budget {fmt(data.summary?.totalBudget)}</span>
          <button onClick={() => setElemView(v => !v)} className="px-2 py-1 rounded font-semibold" data-testid="wc-cbs-elemview-toggle"
            style={{ background: elemView ? '#A855F722' : 'var(--app-bg)', color: elemView ? '#A855F7' : 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
            {elemView ? 'By Cost Element ▾' : 'Total Outflow ▾'}
          </button>
        </div>
      </div>

      {/* Peak funding callout */}
      {peak.month && peak.amount > 0 && (
        <div className="px-3 py-2 flex items-center gap-3 flex-wrap" style={{ background: '#F59E0B10', borderBottom: '1px solid #F59E0B33', color: '#B45309' }} data-testid="wc-peak-funding-callout">
          <span className="text-[11px] font-bold" style={{ color: '#B45309' }}>PEAK FUNDING NEED</span>
          <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
            <b>{peak.month}</b> — cumulative Fcst outflow exceeds cumulative net inflow by <b>{fmt(peak.amount)}</b>. Treasury should size the LC / bridge facility for this month.
          </span>
        </div>
      )}

      {/* Retention controls */}
      <div className="px-3 py-2 flex items-center gap-3 flex-wrap text-[11px]" style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>Retention:</span>
        <label className="flex items-center gap-1" data-testid="wc-retention-pct">
          <input type="range" min="0" max="10" step="0.5" value={retentionPct}
            onChange={e => setRetentionPct(parseFloat(e.target.value))}
            className="accent-orange-500 w-32" />
          <span className="font-mono w-10 text-right" style={{ color: '#F97316' }}>{retentionPct.toFixed(1)}%</span>
        </label>
        <label className="flex items-center gap-1" data-testid="wc-retention-lag">
          <span>release lag</span>
          <select value={retentionLag} onChange={e => setRetentionLag(parseInt(e.target.value))}
            className="rounded px-1 py-0.5 text-[11px]"
            style={{ background: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            {[0, 3, 6, 9, 12, 18, 24].map(n => <option key={n} value={n}>{n} mo</option>)}
          </select>
        </label>
        <span className="ml-auto">
          Total held: <b style={{ color: '#F97316' }}>{fmt(data.retention?.totalHeld)}</b> · released: <b style={{ color: '#22C55E' }}>{fmt(data.retention?.totalReleased)}</b>
        </span>
      </div>

      {/* Main period table (with peak highlight, retention + element split) */}
      <div className="overflow-auto max-h-[340px]">
        <table className="w-full text-xs">
          <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
            <tr>
              <th className="text-left p-2">Period</th>
              {elemView ? elements.map(el => (
                <th key={el.code} className="text-right p-2" style={{ color: el.color }}>{el.label}</th>
              )) : (
                <>
                  <th className="text-right p-2">Plan Out</th>
                  <th className="text-right p-2">Fcst Out</th>
                </>
              )}
              <th className="text-right p-2" style={{ color: '#F59E0B' }}>Fcst Out</th>
              <th className="text-right p-2" style={{ color: '#22C55E' }}>Inflow</th>
              <th className="text-right p-2" style={{ color: '#F97316' }}>Retn Held</th>
              <th className="text-right p-2" style={{ color: '#22C55E' }}>Retn Rel</th>
              <th className="text-right p-2">Net Inflow</th>
              <th className="text-right p-2" style={{ color: '#B45309' }}>Funding Gap</th>
            </tr>
          </thead>
          <tbody>
            {data.periods.map(p => {
              const isPeak = peak.month === p.periodId;
              return (
                <tr key={p.periodId} className="border-t"
                    style={{ borderColor: 'var(--border-color)', background: isPeak ? '#F59E0B18' : undefined }}
                    data-testid={`wc-cbs-period-${p.periodId}`}>
                  <td className="p-2 font-mono text-[10px]">
                    {p.periodId}{isPeak && <span className="ml-1 text-[9px] font-bold px-1 rounded" style={{ background: '#B45309', color: '#fff' }}>PEAK</span>}
                  </td>
                  {elemView ? elements.map(el => (
                    <td key={el.code} className="p-2 text-right" style={{ color: el.color }}>
                      {fmt(p.byElement?.[el.code]?.forecast)}
                    </td>
                  )) : (
                    <>
                      <td className="p-2 text-right">{fmt(p.plannedOutflow)}</td>
                      <td className="p-2 text-right" style={{ color: '#0EA5E9' }}>{fmt(p.forecastOutflow)}</td>
                    </>
                  )}
                  <td className="p-2 text-right font-semibold" style={{ color: '#F59E0B' }}>{fmt(p.forecastOutflow)}</td>
                  <td className="p-2 text-right" style={{ color: '#22C55E' }}>{fmt(p.forecastInflow)}</td>
                  <td className="p-2 text-right" style={{ color: '#F97316' }}>{fmt(p.retentionHeld)}</td>
                  <td className="p-2 text-right" style={{ color: '#22C55E' }}>{fmt(p.retentionReleased)}</td>
                  <td className="p-2 text-right font-semibold" style={{ color: p.netInflowPostRetention >= 0 ? '#22C55E' : '#EF4444' }}>{fmt(p.netInflowPostRetention)}</td>
                  <td className="p-2 text-right font-semibold" style={{ color: p.fundingGap > 0 ? '#B45309' : '#22C55E' }}>{fmt(p.fundingGap)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Attribution */}
      <div className="border-t p-3" style={{ borderColor: 'var(--border-color)' }}>
        <div className="text-[11px] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Attribution — how each CBS row is linked</div>
        <div className="overflow-auto max-h-[220px]">
          <table className="w-full text-xs">
            <thead style={{ background: 'var(--card-bg-alt)' }}>
              <tr>
                <th className="text-left p-2">CBS Code</th>
                <th className="text-right p-2">Budget</th>
                <th className="text-left p-2">MAN / MAT / EQP / SUB</th>
                <th className="text-left p-2">Linked Tasks</th>
                <th className="text-left p-2">Forecast Window</th>
                <th className="text-right p-2">Per-Month</th>
              </tr>
            </thead>
            <tbody>
              {(data.attribution || []).map(a => (
                <tr key={a.costCode} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="p-2 font-mono text-[10px]" style={{ color: '#0EA5E9' }}>{a.costCode}<div className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{a.costCodeName}</div></td>
                  <td className="p-2 text-right">{fmt(a.budget)}</td>
                  <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    <span style={{ color: '#0EA5E9' }}>{Math.round((a.elementShare?.MAN || 0) * 100)}%</span> ·{' '}
                    <span style={{ color: '#F59E0B' }}>{Math.round((a.elementShare?.MAT || 0) * 100)}%</span> ·{' '}
                    <span style={{ color: '#A855F7' }}>{Math.round((a.elementShare?.EQP || 0) * 100)}%</span> ·{' '}
                    <span style={{ color: '#22C55E' }}>{Math.round((a.elementShare?.SUB || 0) * 100)}%</span>
                  </td>
                  <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    <div>{a.linkedTaskIds.length} task{a.linkedTaskIds.length === 1 ? '' : 's'} · uids {a.linkedTaskIds.join(', ')}</div>
                    {a.linkedTaskNames?.length > 0 && <div className="italic truncate max-w-[200px]" title={a.linkedTaskNames.join(', ')}>{a.linkedTaskNames.slice(0, 2).join(', ')}{a.linkedTaskNames.length > 2 ? ` +${a.linkedTaskNames.length - 2}` : ''}</div>}
                  </td>
                  <td className="p-2 text-[10px]" style={{ color: '#0EA5E9' }}>{a.forecast?.start ? `${a.forecast.start} → ${a.forecast.end}` : '—'}</td>
                  <td className="p-2 text-right font-semibold">{fmt(a.forecast?.perMonth || a.planned?.perMonth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
